import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaFileContract,
  FaArrowRight,
  FaEnvelope,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import "./TermsOfServicePage.css";

const TermsOfServicePage = () => {
  const lastUpdated = "January 2025";

  /* Smooth scroll to top on load */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="staffline-terms-page">

      {/* ===== HERO ===== */}
      <section className="staffline-terms-hero">
        <div className="container staffline-terms-hero-content">
          <span className="staffline-terms-hero-tag">
            <FaFileContract /> Legal
          </span>
          <h1 className="staffline-terms-hero-title">
            Terms of <span>Service</span>
          </h1>
          <p className="staffline-terms-hero-subtitle">
            Please read these terms carefully. By subscribing to StaffLine
            Transport, you agree to the rules and conditions outlined below.
          </p>
          <span className="staffline-terms-hero-updated">
            Last Updated: {lastUpdated}
          </span>
        </div>
      </section>

      {/* ===== CONTENT ===== */}
      <section className="staffline-terms-section">
        <div className="container">
          <div className="staffline-terms-content">

            {/* 1. Acceptance of Terms */}
            <div className="staffline-terms-block">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By subscribing to StaffLine Transport Ltd. ("StaffLine",
                "we", "our", "us") or using our shuttle services, you ("the
                subscriber", "you") agree to be bound by these Terms of
                Service. If you do not agree to these terms, please do not
                subscribe to or use our services.
              </p>
            </div>

            {/* 2. Subscription Service */}
            <div className="staffline-terms-block">
              <h2>2. Subscription Service</h2>
              <p>
                StaffLine provides a subscription-based staff shuttle service
                operating Monday to Friday, excluding public holidays. Each
                subscription includes:
              </p>
              <ul>
                <li>An assigned route, bus, and seat number</li>
                <li>A personalised StaffLine ID card</li>
                <li>Daily morning pickup and evening return</li>
                <li>SMS notifications for route updates</li>
              </ul>
            </div>

            {/* 3. Subscription Fees & Payment */}
            <div className="staffline-terms-block">
              <h2>3. Subscription Fees & Payment</h2>
              <p>
                The monthly subscription fee is <strong>800 GHS</strong>, flat
                across all routes, unless otherwise stated. Payment must be
                made in advance for the month of service.
              </p>
              <ul>
                <li>Payments are accepted via Mobile Money, bank transfer, or card</li>
                <li>No cash payments are accepted on the bus</li>
                <li>Subscriptions are non-transferable</li>
                <li>Late payments may result in suspension of service</li>
              </ul>
            </div>

            {/* 4. StaffLine ID Card */}
            <div className="staffline-terms-block">
              <h2>4. StaffLine ID Card</h2>
              <p>
                Every subscriber receives a personal StaffLine ID card that
                must be presented when boarding the bus.
              </p>
              <ul>
                <li>ID cards are non-transferable and must not be shared</li>
                <li>Lost cards must be reported immediately for replacement</li>
                <li>A replacement fee may apply for lost or damaged cards</li>
                <li>Misuse of an ID card may result in termination of service</li>
              </ul>
            </div>

            {/* 5. Pickup & Drop-off Rules */}
            <div className="staffline-terms-block">
              <h2>5. Pickup & Drop-off Rules</h2>
              <p>
                Subscribers must be present at their assigned pickup point at
                the scheduled time. Buses cannot wait for late passengers.
              </p>
              <ul>
                <li>Missed trips are forfeited — no refunds for missed trips</li>
                <li>You may board at a later stop on your route if you miss your pickup</li>
                <li>Only scheduled stops are permitted for boarding and drop-off</li>
                <li>Subscribers must maintain respectful conduct on the bus</li>
              </ul>
            </div>

            {/* 6. Refund & Cancellation Policy */}
            <div className="staffline-terms-block">
              <h2>6. Refund & Cancellation Policy</h2>
              <p>
                We aim to be fair and transparent with our refund policy:
              </p>
              <ul>
                <li>
                  <strong>Cancellation within 7 days of payment:</strong>{" "}
                  Pro-rated refund of unused days.
                </li>
                <li>
                  <strong>Cancellation after 7 days:</strong> No refund for the
                  current month.
                </li>
                <li>
                  <strong>Service disruptions caused by StaffLine:</strong>{" "}
                  Full refund or credit for affected days.
                </li>
                <li>
                  <strong>Force majeure events</strong> (e.g., natural
                  disasters, government orders) — refunds may not apply.
                </li>
              </ul>
            </div>

            {/* 7. Safety & Conduct */}
            <div className="staffline-terms-block">
              <h2>7. Safety & Conduct</h2>
              <p>
                Your safety and that of others is our top priority. The
                following behaviours are strictly prohibited:
              </p>
              <ul className="staffline-terms-list-no">
                <li><FaTimesCircle /> Smoking or vaping on the bus</li>
                <li><FaTimesCircle /> Consumption of alcohol or illegal substances</li>
                <li><FaTimesCircle /> Harassment, abuse, or discrimination of any kind</li>
                <li><FaTimesCircle /> Tampering with CCTV, cameras, or safety equipment</li>
                <li><FaTimesCircle /> Bringing illegal or hazardous items on board</li>
              </ul>
              <p>
                Violation of these rules may result in immediate termination of
                your subscription without refund.
              </p>
            </div>

            {/* 8. StaffLine Responsibilities */}
            <div className="staffline-terms-block">
              <h2>8. StaffLine Responsibilities</h2>
              <p>We commit to:</p>
              <ul className="staffline-terms-list-yes">
                <li><FaCheckCircle /> Operating buses on schedule, Monday to Friday</li>
                <li><FaCheckCircle /> Maintaining clean, air-conditioned, and safe vehicles</li>
                <li><FaCheckCircle /> Vetting and training all drivers</li>
                <li><FaCheckCircle /> Providing SMS updates on route changes or delays</li>
                <li><FaCheckCircle /> Protecting your personal data per our Privacy Policy</li>
              </ul>
            </div>

            {/* 9. Limitation of Liability */}
            <div className="staffline-terms-block">
              <h2>9. Limitation of Liability</h2>
              <p>
                StaffLine is not liable for delays, losses, or damages caused
                by events beyond our reasonable control, including but not
                limited to: traffic congestion, road closures, vehicle
                breakdowns, fuel shortages, strikes, or severe weather.
              </p>
              <p>
                Our maximum liability to any subscriber shall not exceed the
                monthly subscription fee paid for the affected month.
              </p>
            </div>

            {/* 10. Changes to Service */}
            <div className="staffline-terms-block">
              <h2>10. Changes to Service</h2>
              <p>
                We reserve the right to modify routes, schedules, pickup
                points, pricing, or any aspect of the service with reasonable
                notice. Subscribers will be notified via SMS or email of any
                material changes.
              </p>
            </div>

            {/* 11. Termination */}
            <div className="staffline-terms-block">
              <h2>11. Termination</h2>
              <p>
                Either party may terminate the subscription with reasonable
                notice. StaffLine may terminate immediately in cases of:
              </p>
              <ul>
                <li>Non-payment of subscription fees</li>
                <li>Violation of safety or conduct rules</li>
                <li>Fraudulent use of another person's ID card</li>
                <li>Abuse of our staff or fellow passengers</li>
              </ul>
            </div>

            {/* 12. Governing Law */}
            <div className="staffline-terms-block">
              <h2>12. Governing Law</h2>
              <p>
                These Terms of Service are governed by the laws of the
                Republic of Ghana. Any disputes arising will be resolved
                through good-faith negotiation, and if necessary, the
                appropriate courts of Ghana.
              </p>
            </div>

            {/* 13. Contact Us */}
            <div className="staffline-terms-block staffline-terms-block-contact">
              <h2>13. Contact Us</h2>
              <p>
                If you have questions about these Terms of Service, please
                contact us:
              </p>
              <div className="staffline-terms-contact-card">
                <a href="mailto:info@stafflinetransport.com">
                  <FaEnvelope />
                  stafflinetransportgh@gmail.com
                </a>
              </div>
            </div>

            {/* Back to Home CTA */}
            <div className="staffline-terms-back">
              <Link to="/" className="staffline-terms-back-btn">
                Back to Home <FaArrowRight />
              </Link>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default TermsOfServicePage;